import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { departmentApi } from '@/entities/Department';
import type { Department } from '@/entities/Department';

export const useDepartmentStore = defineStore('department-management', () => {
  const departments = ref<Department[]>([]);
  const currentDepartment = ref<Department | null>(null);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const error = ref<string | null>(null);

  const departmentCount = computed(() => departments.value.length);
  const hasDepartments = computed(() => departments.value.length > 0);
  const activeDepartments = computed(() => departments.value.filter((d) => d.active));

  async function fetchAllDepartments(): Promise<boolean> {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await departmentApi.getAll();
      departments.value = response ?? [];
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch departments';
      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchDepartmentById(departmentId: number): Promise<Department | null> {
    try {
      isLoading.value = true;
      error.value = null;
      const department = await departmentApi.getById(departmentId);
      currentDepartment.value = department;
      return department;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch department';
      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  }

  async function createDepartmentWithPositions(
    code: string,
    positionIds?: number[]
  ): Promise<boolean> {
    try {
      isCreating.value = true;
      error.value = null;
      const newDepartment = await departmentApi.create({ code, positionIds });
      departments.value.push(newDepartment);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Failed to create department';
      return false;
    } finally {
      isCreating.value = false;
    }
  }

  function setDepartments(newDepartments: Department[]) {
    departments.value = newDepartments;
  }

  function clearError() {
    error.value = null;
  }

  function clearCurrentDepartment() {
    currentDepartment.value = null;
  }

  function reset() {
    departments.value = [];
    currentDepartment.value = null;
    isLoading.value = false;
    error.value = null;
  }

  return {
    departments: readonly(departments),
    currentDepartment: readonly(currentDepartment),
    isLoading: readonly(isLoading),
    isCreating: readonly(isCreating),
    error: readonly(error),
    departmentCount,
    hasDepartments,
    activeDepartments,
    fetchAllDepartments,
    fetchDepartmentById,
    createDepartmentWithPositions,
    setDepartments,
    clearError,
    clearCurrentDepartment,
    reset,
  };
});
